import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaNewspaper, FaArrowRight, FaCalendarAlt } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const categoryColor = (cat) => {
  const map = {
    Prestasi: 'bg-green-100 text-green-700',
    Event: 'bg-blue-100 text-blue-700',
    Kolaborasi: 'bg-purple-100 text-purple-700',
    Program: 'bg-amber-100 text-amber-700',
  };
  return map[cat] || 'bg-gray-100 text-gray-700';
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });

const NewsPage = () => {
  usePageTitle();
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('news')
      .select('id, title, slug, excerpt, cover_url, category, created_at')
      .eq('is_published', true)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setArticles(data || []); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FaNewspaper className="text-gold-400" /> {t('news.title')}
          </h1>
          <p className="text-gray-300">{t('news.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {articles.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📰</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('news.noNews')}</h2>
            <p className="text-gray-500">{t('news.noNewsDesc')}</p>
          </div>
        ) : (
          <>
            {/* Featured article */}
            {featured && (
              <Link to={`/berita/${featured.slug}`} className="block bg-white rounded-2xl shadow-md overflow-hidden mb-10 border border-gray-100 group">
                <div className="md:flex">
                  <div className="md:w-2/5 bg-gradient-to-br from-primary-700 to-primary-900 flex items-center justify-center overflow-hidden">
                    {featured.cover_url ? (
                      <img src={featured.cover_url} alt={featured.title} className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="p-10"><FaNewspaper className="text-gold-400 text-7xl opacity-50" /></div>
                    )}
                  </div>
                  <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                    <span className={`inline-block w-fit text-xs font-semibold px-3 py-1 rounded-full mb-3 ${categoryColor(featured.category)}`}>
                      {featured.category}
                    </span>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-700 transition-colors">{featured.title}</h2>
                    <p className="text-gray-500 mb-4 leading-relaxed line-clamp-3">{featured.excerpt}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <FaCalendarAlt /> {formatDate(featured.created_at)}
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map(n => (
                <Link key={n.id} to={`/berita/${n.slug}`} className="group bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all overflow-hidden">
                  {n.cover_url ? (
                    <img src={n.cover_url} alt={n.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="h-2 bg-gradient-to-r from-primary-500 to-gold-500"></div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColor(n.category)}`}>
                        {n.category}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1"><FaCalendarAlt /> {formatDate(n.created_at)}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
                      {n.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-3 mb-4">{n.excerpt}</p>
                    <span className="text-primary-600 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t('news.readMore')} <FaArrowRight className="text-xs" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
